import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';

const ClassesHelpModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState([]);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!isOpen) setExpandedSections([]);
  }, [isOpen]);

  const toggleSection = (sectionId) => {
    const isExpanding = !expandedSections.includes(sectionId);
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    if (isExpanding) {
      setTimeout(() => {
        sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  };

  if (!isOpen) return null;

  const sections = [
    {
      id: 'how-terms-work',
      title: '1. How Terms Work',
      content: (
        <>
          <p><strong>What is a term?</strong> A term is a date-bounded period that groups your classes (for example, "Term 1 2026: Jan 27 – Mar 28"). All classes and their sessions belong to a term. Students enrol into classes within a specific term.</p>
          <p><strong>Creating a term:</strong> Go to Terms &amp; Classes and click "New Term". Set a name, start date, and end date. You can have multiple terms active at the same time (e.g., one term for adult classes and another for kids).</p>
          <p><strong>Rolling over a term:</strong> The "New Term" wizard guides you through three steps: set term dates, copy classes from a previous term, and optionally roll over confirmed students into the new term. Each rolled-over student gets an Unconfirmed enrolment that appears on your roster. After creating the term, the wizard offers an optional email review step so you can notify rolled-over students straight away.</p>
          <p><strong>Deleting a term:</strong> A term can only be deleted if it has no classes with active enrolments. Archive old terms by keeping them in the system. They remain visible for reporting.</p>
        </>
      )
    },
    {
      id: 'creating-classes',
      title: '2. Creating Classes',
      content: (
        <>
          <p><strong>Creating a class:</strong> Inside a term, click "+ New Class". Fill in the class name, dance style (e.g., Salsa, Bachata), level (Beginner / Intermediate / Advanced / All Levels), day of the week, start time, end time, location, and an optional max student cap.</p>
          <p><strong>How classes run:</strong> Each class runs on its set day throughout the term: one session per week on that day. Sessions are generated automatically for the term's date range.</p>
          <p><strong>Copying classes between terms:</strong> When starting a new term, use the "Copy from previous term" option to import class definitions. This saves re-entering schedules. Just review and confirm before saving.</p>
          <p><strong>Term classes vs short courses vs drop-ins:</strong> These distinctions are used in Pricing (to match pricing options to the right class type) rather than being set on the class itself.</p>
        </>
      )
    },
    {
      id: 'enrolments-checkin',
      title: '3. Enrolments & Check-In',
      content: (
        <>
          <p><strong>Enrolling students manually:</strong> You can enrol a student from two places: the <strong>Manage Students</strong> card on the Classes page (open the student profile and add an enrolment) or the check-in screen (use the "Add Student" button). Both flows let you set a leader/follower role at the time of enrolment.</p>
          <p><strong>Leader/follower role:</strong> Leader and follower are assigned <em>per enrolment</em>, not stored on the student. The same student can be a leader in one class and a follower in another. You set (or skip) the role when enrolling them.</p>
          <p><strong>Enrolment statuses:</strong> Enrolments show as <strong>Confirmed</strong> (green) or <strong>Unconfirmed</strong> (red). New enquiries from your public page appear in the Students &gt; <strong>Requests</strong> tab for follow-up and conversion.</p>
          <p><strong>Attendance tracking:</strong> The system records attendance per session. Walk-in and make-up check-ins are flagged separately in attendance history for easy reporting. Cancelled sessions are also recorded so you can track make-ups owed.</p>
          <p><strong>Upgrades &amp; re-enrolments:</strong> The Students section has dedicated tabs for upgrade requests (e.g., Beginner → Intermediate) and students who haven't re-enrolled in the next term, useful for retention follow-up.</p>
          <p><em>For full details on check-in, walk-ins, make-ups, and session cancellations — see the help icon on the Check-In page.</em></p>
        </>
      )
    },
    {
      id: 'student-upload',
      title: '4. Student Upload (Bulk Import)',
      content: (
        <>
          <p>The <strong>Student Upload</strong> card on the Manage Classes page opens the bulk CSV import tool. Use it when migrating students from another system or onboarding a whole term at once. For one-off enrolments, use the <strong>Manage Students</strong> card on the Classes page instead. Step-by-step guidance lives on the Bulk Import page itself.</p>
        </>
      )
    },
    {
      id: 'url-groups-public-page',
      title: '5. Your School URL & Public Pages',
      content: (
        <>
          <p>Your school has a public URL where prospective students can browse your classes and submit an enrolment enquiry. No account required. You can also create URL groups to make focused pages for specific audiences (e.g., just Salsa classes, or just short courses).</p>
          <p>To set up your school URL and manage URL groups, go to <strong>Settings</strong>. The help icon there has a full guide on how it all works.</p>
        </>
      )
    },
    {
      id: 'pricing-options',
      title: '6. Pricing Options',
      content: (
        <>
          <p><strong>Where pricing lives:</strong> Go to Pricing to configure how students pay. Pricing options are school-wide and not tied to individual classes.</p>
          <p><strong>Payment type:</strong> Each pricing option has a payment frequency: <strong>Once</strong> (one-time upfront), <strong>Weekly</strong>, <strong>Fortnightly</strong>, or <strong>Monthly</strong>. Once is the standard for term fees. Recurring types (weekly/fortnightly/monthly) are stored in the system but require direct debit setup to activate. They show a "Contact studio to arrange" note on the public page.</p>
          <p><strong>Class type:</strong> Each pricing option can be scoped to one of four types:</p>
          <ul>
            <li><strong>All class types:</strong> applies to any enrolment regardless of type. Ideal for subscription or membership plans where a student pays a recurring weekly/monthly fee and may attend multiple class types. The system will offer this option alongside type-specific ones.</li>
            <li><strong>Term class:</strong> only offered when a student is enrolled in a weekly recurring term class. Use this for your standard per-term fees.</li>
            <li><strong>Short course:</strong> only offered for short course enrolments. Use this for your short course fees.</li>
            <li><strong>Drop-in:</strong> only offered for drop-in class enrolments.</li>
          </ul>
          <p><em>Tip: If a student is enrolled in both a term class and a short course at the same time, the payment screen will show both type groups separately so you can collect the right fee for each, or a combined QR covering both.</em></p>
          <p><strong>Max classes:</strong> Set a cap on how many sessions the option covers, or leave it unlimited.</p>
          <p><strong>Covers:</strong> A label (e.g., "Beginner", "L2+", "All") describing which level or tier the option is for, useful when you have different pricing for different levels.</p>
          <p><strong>Active toggle:</strong> Only active options appear in the student payment flow. Deactivate an option to hide it without deleting it.</p>
        </>
      )
    },
    {
      id: 'branding-configuration',
      title: '7. Branding Configuration',
      content: (
        <>
          <p><strong>Where to configure branding:</strong> Studio branding (colour theme for public pages) is configured in <strong>Settings</strong> → Branding.</p>
          <p><strong>What branding controls:</strong> Colours for buttons, headings, and highlights on your public class enrolment pages. Future booking flows will also use these colours.</p>
          <p><em>Tip: After updating your colours, visit your public group URL to preview how it looks to prospective students.</em></p>
        </>
      )
    },
    {
      id: 'communications',
      title: '8. Send Emails',
      content: (
        <>
          <p><strong>What is Send Emails?</strong> The Send Emails portal lets you compose and broadcast personalised emails to your students directly from the platform — no third-party mail tool needed.</p>
          <p><strong>Audience options:</strong> Choose all students, all students in a specific class (filtered by term first), all students in a term, all students enrolled in a particular dance style, or a custom multi-select of individual students. The recipient count is shown live below the audience picker so you can confirm before sending.</p>
          <p><strong>Compose tab:</strong> Write your subject and use the rich-text editor for the body. Click <strong>+ Insert Field</strong> in the toolbar to drop personalisation chips for <em>Student Name</em>, <em>Studio Name</em>, <em>Class Name</em>, or <em>Term Name</em>. These resolve to each recipient&rsquo;s actual values when the email is delivered. Click the image icon to insert a picture inline; resize by dragging the corner handles. Optionally load a starting point from your saved email templates.</p>
          <p><strong>Sent tab:</strong> Shows a history of all broadcasts you have sent including the date, subject, audience, recipient count, delivery status pill (Sending… / Delivered / Partial / Failed), and sender name. The status updates as each email completes so you can see actual delivery outcomes, not just optimistic counts.</p>
          <p><strong>Email templates:</strong> You can customise the subject and body of any system email template in <strong>Settings &rarr; Email Templates</strong>. Changes apply to your account only. You can reset to the system default at any time.</p>
          <p><em>Send Emails is accessible from both the Dashboard and the Classes portal.</em></p>
        </>
      )
    }
  ];

  return (
    <div className="classes-help-overlay" onClick={onClose}>
      <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="classes-help-header">
          <div className="classes-help-header-content">
            <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
            <h2>Class Management Help</h2>
          </div>
          <button className="classes-help-close" onClick={onClose}>×</button>
        </div>

        {/* Accordion Sections */}
        <div className="classes-help-body">
          {sections.map(section => (
            <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
              <button
                className="help-accordion-header"
                onClick={() => toggleSection(section.id)}
              >
                <Icon
                  path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                  size={0.8}
                  color="var(--dl-color-purple-default)"
                />
                <span>{section.title}</span>
              </button>
              {expandedSections.includes(section.id) && (
                <div className="help-accordion-content">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="classes-help-footer">
          <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
        </div>
      </div>
    </div>
  );
};

export default ClassesHelpModal;
